import { useEffect, useState } from "react";
import { api, money, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { Download, ReceiptText, Loader2 } from "lucide-react";

const ALL = "__all__";
const iso = (d) => d.toISOString().slice(0, 10);

export default function VisitValueReport({ cur = "EUR" }) {
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getFullYear(), today.getMonth() - 2, 1)));
  const [to, setTo] = useState(iso(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
  const [customerId, setCustomerId] = useState("");
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const params = { date_from: from || undefined, date_to: to || undefined, customer_id: customerId || undefined };
    api.get("/reports/visit-values", { params }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [from, to, customerId]);

  const exportCsv = () => {
    if (!data) return;
    const head = ["Job No", "Visit Date", "Client", "Site", "Recurring Work", "Contract Visit Value", "Billing Status", "Invoice No", "Invoiced Amount", "Variance"];
    const lines = [head.join(",")];
    data.rows.forEach((r) => lines.push([
      r.job_ref, r.visit_date || "", `"${r.customer_name || ""}"`, `"${r.site_name || ""}"`,
      `"${(r.template_ref || "")} ${(r.template_title || "").replace(/"/g, "'")}"`,
      r.visit_value, r.billing_status || "", r.invoice_number || "",
      r.invoiced_amount ?? "", r.variance ?? "",
    ].join(",")));
    const t = data.totals;
    lines.push(["\"TOTALS\"", "", "", "", "", t.contract_value, "", "", t.invoiced, t.variance].join(","));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `visit-values-${from}_${to}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const t = data?.totals;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="visit-value-report">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="font-head font-semibold flex items-center gap-2"><ReceiptText className="h-4 w-4 accent-text" />Visit Values vs Invoiced</h3>
          <p className="text-xs text-slate-500">Every generated recurring visit with its contract value against what was actually invoiced.</p>
        </div>
        <Button variant="outline" size="sm" className="h-9" data-testid="vv-export" onClick={exportCsv} disabled={!data || data.rows.length === 0}>
          <Download className="h-4 w-4 mr-1" />Download CSV
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 max-w-3xl">
        <div><Label className="text-[11px] text-slate-500">From</Label>
          <Input type="date" data-testid="vv-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-9 bg-white" /></div>
        <div><Label className="text-[11px] text-slate-500">To</Label>
          <Input type="date" data-testid="vv-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9 bg-white" /></div>
        <div className="col-span-2"><Label className="text-[11px] text-slate-500">Client</Label>
          <Select value={customerId || ALL} onValueChange={(v) => setCustomerId(v === ALL ? "" : v)}>
            <SelectTrigger data-testid="vv-customer" className="mt-1 h-9 bg-white"><SelectValue placeholder="All clients" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              <SelectItem value={ALL}>All clients</SelectItem>
              {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select></div>
      </div>

      {loading && <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>}
      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            {[["Contract value", money(t.contract_value, cur), "text-slate-800"],
              ["Invoiced", money(t.invoiced, cur), "text-emerald-600"],
              ["Not yet invoiced", money(t.not_invoiced, cur), "text-amber-600"],
              ["Variance", money(t.variance, cur), t.variance < 0 ? "text-red-600" : "text-emerald-600"]].map(([l, v, cls]) => (
              <div key={l} className="bg-slate-50 rounded-md p-3">
                <div className="text-xs text-slate-500">{l}</div>
                <div className={`text-xl font-head font-bold mt-0.5 ${cls}`} data-testid={`vv-kpi-${l.toLowerCase().replace(/ /g, "-")}`}>{v}</div>
              </div>
            ))}
          </div>
          <div className="max-h-[340px] overflow-y-auto scroll-thin border border-slate-100 rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0"><tr>{["Job No", "Date", "Client", "Site", "Recurring Work", "Visit Value", "Billing", "Invoice No", "Invoiced", "Variance"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-2.5 py-2 text-xs whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400">No generated visits in this range.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.job_ref} className="border-t border-slate-100" data-testid={`vv-row-${r.job_ref}`}>
                    <td className="px-2.5 py-2 font-medium text-slate-800 whitespace-nowrap">{r.job_ref}</td>
                    <td className="px-2.5 py-2 text-slate-600 whitespace-nowrap">{r.visit_date ? fmtDate(r.visit_date) : "—"}</td>
                    <td className="px-2.5 py-2 text-slate-600">{r.customer_name}</td>
                    <td className="px-2.5 py-2 text-slate-500">{r.site_name || "—"}</td>
                    <td className="px-2.5 py-2 text-slate-500 text-xs">{r.template_ref} · {r.template_title}</td>
                    <td className="px-2.5 py-2 text-slate-700 whitespace-nowrap">{r.visit_value ? money(r.visit_value, cur) : "—"}</td>
                    <td className="px-2.5 py-2"><StatusBadge status={r.billing_status} /></td>
                    <td className="px-2.5 py-2 text-slate-500">{r.invoice_number || "—"}</td>
                    <td className="px-2.5 py-2 text-slate-700 whitespace-nowrap">{r.invoiced_amount != null ? money(r.invoiced_amount, cur) : "—"}</td>
                    <td className={`px-2.5 py-2 whitespace-nowrap font-medium ${r.variance == null ? "text-slate-400" : r.variance < 0 ? "text-red-600" : "text-emerald-600"}`}>{r.variance != null ? money(r.variance, cur) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
